import { Button, Dialog, DialogActions, Box, DialogContent, IconButton, DialogTitle, Typography, FormHelperText, TextField, Tooltip, Switch, Accordion, AccordionSummary, AccordionDetails, Alert, Divider } from '@mui/material';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { CustomErrorLogger } from '../../utils/CustomErrorLogger';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import Iconify from '../Iconify';
import * as Yup from 'yup';
import axios from 'axios';
import '../../css/users/addUserDialog.css';

export const AddRoleDialog = ({ openAddRole, setOpenAddRole, setOpenSuccessfulAddRole, setRoleId, roles, setRoles, totalRoles, setTotalRoles, filteredRolesCount, setFilteredRolesCount }) => {

    const { t } = useTranslation();

    const [selectedCredentialIds, setSelectedCredentialIds] = useState(new Set());
    const [allCredentials, setAllCredentials] = useState([]);
    const [errMsg, setErrMsg] = useState(null);

    const formik = useFormik({
        initialValues: {
            name: '',
            description: '',
        },
        validationSchema: Yup.object().shape({
            name: Yup.string().required(t('isRequired')),
            description: Yup.string().optional(),
        }),
        onSubmit: async (values) => {
            try {
                const bodyData = {
                    name: values.name,
                    description: values.description,
                    selectedCredentialIds: Array.from(selectedCredentialIds),
                };
                const response = await axios.post('role', bodyData);
                if (response.status === 200 || response.status === 201) {
                    setRoleId(response.data.id);
                    setRoles([...roles, response.data]);
                    setTotalRoles(totalRoles + 1);
                    setFilteredRolesCount(filteredRolesCount + 1);
                    setOpenAddRole(false);
                    setErrMsg(null)
                    setOpenSuccessfulAddRole(true);
                }
            } catch (error) {
                setErrMsg(error.message || 'erreur lors de l\'ajout du rôle')
                CustomErrorLogger(error, "error adding role in AddRoleDialog");
                console.error('Error adding role:', error);
            }
        },
    });

    const { handleSubmit, values, handleChange, handleBlur, touched, errors } = formik;

    const handleClose = useCallback(() => {
        setOpenAddRole(false);
        setErrMsg(null)
    }, [setOpenAddRole]);

    // Fetch credentials once on component mount
    useEffect(() => {
        const fetchCredentials = async () => {
            try {
                const response = await axios.get('credential');
                if (response.status === 200 || response.status === 201) {
                    setAllCredentials(response.data);
                }
            } catch (error) {
                console.error(error, "error fetching credentials in AddRoleDialog");
                CustomErrorLogger(error, "error fetching credentials in AddRoleDialog");
            }
        };
        fetchCredentials();
    }, []);

    const handleSwitchChange = useCallback((credential, checked) => {

        setSelectedCredentialIds((prevIds) => {
            const newIds = new Set(prevIds);
            if (newIds.has(credential.id) && !checked) {
                newIds.delete(credential.id);
            } else {
                // Add the current credential and check its parents
                newIds.add(credential.id);
            }
            return newIds;
        });
    }, []);

    // Function to generate tooltip content
    const generateTooltipContent = (credential) => {
        return (
            <Box>
                {credential?.description && (
                    <Typography variant="caption" color="textSecondary">{credential.description}</Typography>
                )}
            </Box>
        );
    };

    // Memoize credentials list for rendering
    const credentialsList = useMemo(() => {
        return allCredentials?.map((credentialGrp, index) => (
            <Accordion fullWidth key={index} sx={{ width: '100%', backgroundColor: 'transparent', border: 'none', boxShadow: 'none', '&:before': { display: 'none' } }}>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ margin: '0 0 10px 0', padding: 0, backgroundColor: '#ebeef4', borderRadius: '5px', display: 'flex', flexDirection: 'row-reverse', gap: '5px' }}
                >
                    <Tooltip title={credentialGrp.groupDescription} arrow>
                        <Typography variant="h6" component="div">{credentialGrp.groupName}</Typography>
                    </Tooltip>
                </AccordionSummary>
                <AccordionDetails sx={{ margin: 0, padding: 0 }}>
                    {credentialGrp.credentials?.map(credential => (
                        <Box key={credential.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', my: 2 }}>
                            <Tooltip title={generateTooltipContent(credential)} arrow>
                                <Typography>{credential?.name}</Typography>
                            </Tooltip>
                            <Switch
                                checked={selectedCredentialIds.has(credential.id)}
                                onChange={(e) => handleSwitchChange(credential, e.target.checked)}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                        color: '#EA5E00', // Change the color of the switch when checked
                                    },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                        backgroundColor: '#EA5E00', // Change the color of the track when checked
                                    },
                                }}
                            />
                        </Box>
                    ))}
                </AccordionDetails>
            </Accordion>
        ));
    }, [allCredentials, handleSwitchChange, selectedCredentialIds]);


    return (
        <Dialog open={openAddRole} fullWidth>
            <IconButton onClick={handleClose} className='addUser_icon_button'>
                <Iconify icon={"material-symbols:close"} className='addUser_close_icon' />
            </IconButton>
            <DialogTitle className='addUser_dialog_title'>{t('addRoleDialogeTitle')}</DialogTitle>
            <Divider />
            {errMsg && <Alert key={errMsg} severity="error">{errMsg}</Alert>}
            <form onSubmit={handleSubmit}>
                <DialogContent className='addUser_dialog_content'>
                    <Typography className='addUser_dialog_SubTitle'>{t('addRoleDialogeSubTitle')}</Typography>
                    <Box className="addUser_helper_selct_box">
                        <FormHelperText id="outlined-weight-helper-text" className="addUserInputTitle">{t('Nom')}</FormHelperText>
                        <TextField
                            fullWidth
                            value={values.name}
                            className='addUser_InputField'
                            id="outlined-adornment-weight"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            name="name"
                            error={touched.name && Boolean(errors.name)}
                            helperText={touched.name && errors.name}
                            aria-describedby="outlined-weight-helper-text"
                        />
                    </Box>
                    <Box className="addUser_helper_selct_box">
                        <FormHelperText id="outlined-weight-helper-text" className="addUserInputTitle">{t('Description')} (optionnel)</FormHelperText>
                        <TextField
                            fullWidth
                            value={values.description}
                            className='addUser_InputField'
                            id="outlined-adornment-weight"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            name="description"
                            error={touched.description && Boolean(errors.description)}
                            helperText={touched.description && errors.description}
                            aria-describedby="outlined-weight-helper-text"
                        />
                    </Box>
                    <Typography className='addUserInputTitle' sx={{ marginBottom: '15px' }}>{t("Gérer les autorisations du rôle")} :</Typography>

                    {credentialsList}
                </DialogContent>

                <DialogActions className='addUser_dialog_actions'>
                    <Button className='addUser_button_annuler' onClick={handleClose}>{t('Annuler')}</Button>
                    <Button className='addUser_button_valider' variant="contained" type="submit" disabled={Array.from(selectedCredentialIds).length === 0}>{t('Valider')}</Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};